import styled from "styled-components";
import tw from "twin.macro";
import GradientButton from "../../../../components/GradientButton";
import { COLORS } from "../theme";

const SectionFive = styled.section`
  ${tw`relative flex items-center`}
  z-index: 1;
  min-height: 80vh;
  background: #fff;
`;

const SectionFiveInner = styled.div`
  ${tw`mx-auto`}
  width: 100%;
  padding: 2rem var(--app-content-gutter) 4rem;
  max-width: 1440px;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    padding: 5.25rem 34px 5rem;
  }
`;

const SectionFiveMobileIntro = styled.p`
  display: none;

  @media (max-width: 767px) {
    display: block;
    order: 1;
    margin: 0 auto 5.4rem;
    width: min(100%, 470px);
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    line-height: 1.38;
    font-weight: 400;
    color: #4d4b4b;
    text-transform: uppercase;
    text-align: left;

    strong {
      color: #437672;
      font-weight: 700;
    }
  }
`;

const SectionFiveTitle = styled.h3`
  margin: 0 auto;
  font-family: "Montserrat", sans-serif;
  font-size: 20px;
  line-height: 1.12;
  font-weight: 400;
  color: #555;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;

  strong {
    color: ${COLORS.orange};
    font-weight: 700;
  }

  @media (max-width: 1180px) {
    white-space: normal;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const SectionFiveMobileTitle = styled.h3`
  display: none;

  @media (max-width: 767px) {
    display: block;
    width: min(100%, 430px);
    margin: 3.85rem auto 0;
    font-family: "Montserrat", sans-serif;
    font-size: 14px;
    line-height: 1.28;
    font-weight: 400;
    color: #555;
    text-transform: uppercase;
    text-align: left;

    strong {
      color: ${COLORS.orange};
      font-weight: 700;
    }
  }
`;

const SectionFiveFrame = styled.div`
  position: relative;
  margin-top: 3.4rem;
  padding: 0 2.5rem 4.6rem;

  @media (max-width: 1023px) {
    margin-top: 2.4rem;
    padding: 0 0 4.5rem;
  }

  @media (max-width: 767px) {
    order: 2;
    width: min(100%, 470px);
    margin: 0 auto;
    padding: 2.1rem 0 0;
  }

  @media (min-width: 1024px) and (max-width: 1366px) {
    margin-top: 2.8rem;
  }
`;

const SectionFiveOutline = styled.div`
  position: absolute;
  left: 8rem;
  right: 8rem;
  top: 90px;
  height: 280px;
  border: 1px solid ${COLORS.orange};
  border-radius: 20px;
  z-index: 0;

  @media (max-width: 1023px) {
    display: none;
  }

  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 1024px) and (max-width: 1366px) {
    left: 7rem;
    right: 7rem;
    top: 74px;
    height: 236px;
  }
`;

const SectionFiveCards = styled.div`
  ${tw`grid gap-6 relative`}
  z-index: 1;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 280px));
    justify-content: center;
    column-gap: 1rem;
    align-items: start;
  }

  @media (min-width: 1024px) and (max-width: 1366px) {
    grid-template-columns: repeat(4, minmax(0, 220px));
    column-gap: 0.75rem;
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.75rem 1.6rem;
    padding: 0 0.75rem;

    &::before {
      content: "";
      position: absolute;
      left: 34px;
      right: 34px;
      top: -2.1rem;
      bottom: -2.1rem;
      border: 1px solid ${COLORS.orange};
      border-radius: 13px;
      z-index: 0;
      pointer-events: none;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }
`;

const SectionFiveCard = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 18px;
`;

const SectionFiveButtons = styled.div`
  ${tw`flex flex-col items-center gap-4 relative`}
  z-index: 1;
  padding-top: 3.8rem;

  @media (max-width: 767px) {
    align-items: flex-start;
    width: min(100%, 470px);
    margin: 1.95rem auto 0;
    padding-top: 0;
    gap: 1.45rem;
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: center;
    gap: 7rem;
    width: min(660px, calc(100% - 10rem));
    margin: -0.1rem auto 0;
  }

  @media (min-width: 1024px) and (max-width: 1366px) {
    gap: 3rem;
    width: min(520px, calc(100% - 10rem));
    padding-top: 3.8rem;
  }
`;

const SectionFiveButton = styled(GradientButton).attrs({
  as: "button",
  $variant: "teal",
})`
  min-width: 300px;
  padding: 12px 28px;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 1.1;
  font-weight: 500;
  white-space: nowrap;
  height: 48px;

  @media (max-width: 767px) {
    min-width: 0;
    width: fit-content;
    height: 42px;
    padding: 8px 28px;
    font-size: 14px;
  }
`;

export {
  SectionFive,
  SectionFiveInner,
  SectionFiveMobileIntro,
  SectionFiveTitle,
  SectionFiveMobileTitle,
  SectionFiveFrame,
  SectionFiveOutline,
  SectionFiveCards,
  SectionFiveCard,
  SectionFiveButtons,
  SectionFiveButton,
};
